// App.js - 사용자 정보 입력 모달 완전 구현 버전
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CustomerManagement from './CustomerManagement';
import LandingPage from './components/LandingPage';
import BrowserModal from './modals/BrowserModal';
import { generateDeviceId, isMobile, isKakaoInAppBrowser, openInExternalBrowser } from './utils/deviceUtils';
import { getDeviceLinkInfo, trackShareReferral, sendApprovalEmail } from './firebase';
import { db, COLLECTIONS, DEVICE_STATUS, createDeviceDocument } from './firebase';
import { pageVariants, spinnerVariants } from './utils/animationUtils';
// usePushNotification 훅 제거됨 - firebase.js의 함수들 직접 사용

const App = () => {
  const [appStatus, setAppStatus] = useState('landing');
  // 업종 선택 로직 제거
  const [trialInfo, setTrialInfo] = useState(null);
  const [showUserInfoModal, setShowUserInfoModal] = useState(false);
  const [showBrowserModalLanding, setShowBrowserModalLanding] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountInfo, setDiscountInfo] = useState(null);
  const [booting, setBooting] = useState(true);
  const [userInfo, setUserInfo] = useState({
    name: '', phone: '', email: '', company: '', position: ''
  });
  const [rejectedMeta, setRejectedMeta] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rejectedTrialInfo, setRejectedTrialInfo] = useState(null);

  // 푸시 알림 관련 상태 (직접 관리)
  const [token, setToken] = useState(null);

  // 공유 링크 파라미터 처리
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refDeviceId = urlParams.get('ref');

    if (refDeviceId) {
      // 공유 링크로 접속한 경우 referrer 저장
      localStorage.setItem('shareReferrer', refDeviceId);

      // intro 페이지가 아닌 경우 intro로 리다이렉트
      if (window.location.pathname !== '/intro') {
        // ref 파라미터를 유지한 채로 intro로 이동
        window.location.href = `/intro?ref=${refDeviceId}`;
        return;
      }

      // URL에서 ref 파라미터 제거 (깔끔한 URL 유지)
      urlParams.delete('ref');
      const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  // 온보딩 완료 후 공유 추적
  useEffect(() => {
    let isTracking = false; // 중복 요청 방지
    let attemptCount = 0;
    const MAX_ATTEMPTS = 3; // 최대 시도 횟수

    const checkOnboardingAndTrackShare = async () => {
      const onbCompleted = localStorage.getItem('onb_completed');
      const shareReferrer = localStorage.getItem('shareReferrer');
      const shareTracked = sessionStorage.getItem('shareTracked');

      // 이미 추적한 경우 중단
      if (shareTracked === '1') {
        return;
      }

      // 온보딩 완료 + 공유 링크로 접속 + 아직 추적 안함 + 중복 요청 방지 + 모바일 체크
      if (onbCompleted === '1' && shareReferrer && !shareTracked && !isTracking && isMobile()) {
        isTracking = true;
        attemptCount++;

        try {
          const visitorDeviceId = generateDeviceId();
          const result = await trackShareReferral({
            referrerDeviceId: shareReferrer,
            visitorDeviceId
          });

          // 추적 성공 여부와 관계없이 추적 완료로 마킹
          // (공유자 정보가 없는 경우도 정상적인 케이스일 수 있음)
          sessionStorage.setItem('shareTracked', '1');

          if (result.data && result.data.success) {
            // 추적 성공: 중복이거나 정상 추적
            if (result.data.isDuplicate) {
              console.log('이미 추적된 공유입니다.');
            } else {
              console.log('공유 추적이 완료되었습니다.');
            }
          } else {
            // 추적 실패: 공유자 정보가 없거나 다른 오류
            const errorMsg = result.data?.message || '공유 추적에 실패했습니다.';
            console.warn('공유 추적 실패 (계속 진행):', errorMsg);
          }

          // 공유 링크로 접속한 경우, 사용자가 이미 앱을 사용 중이었는지 확인
          // (referralSource가 있으면 이미 앱을 사용 중이었던 사용자)
          const referralSource = localStorage.getItem('referralSource');
          const isExistingUser = !!referralSource;

          // 공유 링크로 접속한 경우 intro 페이지에 머물도록 함 (이미 intro로 리다이렉트됨)
          // 기존 사용자는 현재 작업을 계속할 수 있도록 페이지 이동하지 않음
          if (isExistingUser) {
            // 기존 사용자의 경우, 공유 추적은 완료했지만 페이지 이동은 하지 않음
            // 사용자가 원할 때 공유 프로모션 버튼을 통해 접근할 수 있음
            console.log('기존 사용자: 공유 추적 완료, 페이지 이동하지 않음');
          }
          // 신규 사용자는 이미 intro 페이지로 리다이렉트되었으므로 추가 이동 불필요
        } catch (error) {
          console.error('공유 추적 오류:', error);

          // 네트워크 오류 등으로 추적 실패
          if (attemptCount >= MAX_ATTEMPTS) {
            // 최대 시도 횟수 초과 시 추적 완료로 마킹
            sessionStorage.setItem('shareTracked', '1');

            // 기존 사용자가 아닌 경우에만 공유 프로모션 페이지로 이동
            const referralSource = localStorage.getItem('referralSource');
            const isExistingUser = !!referralSource;

            if (!isExistingUser && isMobile()) {
              window.location.href = '/share-promotion';
              console.warn('공유 추적 최대 시도 횟수 초과, 페이지 이동 계속 진행');
            } else {
              console.warn('공유 추적 최대 시도 횟수 초과, 기존 사용자이므로 페이지 이동하지 않음');
            }
          } else {
            // 재시도 가능한 경우에는 추적 완료로 마킹하지 않음
            // 다음 interval에서 재시도
          }
        } finally {
          isTracking = false;
        }
      }
    };

    // 주기적으로 확인 (온보딩 완료를 기다림)
    const interval = setInterval(checkOnboardingAndTrackShare, 2000); // 2초 간격으로 변경 (부하 감소)

    // 즉시 한 번 실행
    checkOnboardingAndTrackShare();

    return () => clearInterval(interval);
  }, []);

  // 앱 초기 상태 확인 - Firebase 호출 최소화
  useEffect(() => {
    // 카카오톡 인앱 브라우저 감지 및 외부 브라우저 열기
    if (isKakaoInAppBrowser()) {
      openInExternalBrowser('kakao');
      // 리다이렉트가 일어나면 이후 로직은 실행되지 않거나 중단될 수 있음
      // 하지만 혹시 모를 상황을 대비해 아래 로직도 계속 실행
    }

    const isVerified = localStorage.getItem('userVerified');
    const verificationDate = localStorage.getItem('verificationDate');
    const referralSource = localStorage.getItem('referralSource');

    // 공유 링크로 접속한 경우 처리
    // 온보딩 완료 후 추적 로직(45-115줄)에서 공유 프로모션 페이지로 리다이렉트하므로
    // 여기서는 리다이렉트하지 않고 정상적인 플로우를 따르도록 함
    // 단, 공유 링크로 접속한 사용자는 랜딩 페이지를 거치지 않고 바로 앱으로 진입할 수 있도록 함

    // 유입경로를 한번 선택한 사용자는 영구적으로 랜딩페이지 건너뛰기
    if (referralSource) {
      initializeApp().finally(() => setBooting(false));
      return;
    }

    // 기존 검증 로직 (유입경로가 없는 경우에만)
    if (isVerified === 'true' && verificationDate) {
      const verifiedDate = new Date(verificationDate);
      const now = new Date();
      const daysDiff = Math.floor((now - verifiedDate) / (1000 * 60 * 60 * 24));

      if (daysDiff < 7) {
        initializeApp().finally(() => setBooting(false));
        return;
      }
    }
    setBooting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // body 스크롤 잠금 함수
  const lockBodyScroll = React.useCallback(() => {
    if (showUserInfoModal) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    }
  }, [showUserInfoModal]);

  // 모달이 열릴 때 body 스크롤 막기
  useEffect(() => {
    if (showUserInfoModal) {
      lockBodyScroll();

      // alert 후에도 스크롤 잠금 유지를 위한 인터벌
      const intervalId = setInterval(() => {
        if (showUserInfoModal) {
          lockBodyScroll();
        }
      }, 100);

      return () => {
        clearInterval(intervalId);
        // 스크롤 복원
        const scrollY = document.body.style.top;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        if (scrollY) {
          window.scrollTo(0, parseInt(scrollY || '0') * -1);
        }
      };
    }
  }, [showUserInfoModal, lockBodyScroll]);

  // 체험 상태에서도 상단 버튼으로 승인신청 모달을 띄울 수 있도록 전역 이벤트 리스너
  useEffect(() => {
    const handler = () => {
      console.log('[App] openApprovalModal received');
      setShowUserInfoModal(true);
      // 모달이 열린 후 스크롤 잠금 (alert 후에도 유지)
      setTimeout(() => {
        lockBodyScroll();
      }, 100);
    };
    window.addEventListener('openApprovalModal', handler);
    return () => window.removeEventListener('openApprovalModal', handler);
  }, [showUserInfoModal, lockBodyScroll]);

  // 푸시 알림 초기화 (앱이 활성화된 후)
  useEffect(() => {
    const initializePushNotifications = async () => {
      // active, trial, expired, subscription_expired 사용자도 푸시 알림 수신 가능
      if (appStatus === 'active' || appStatus === 'trial' || appStatus === 'expired' || appStatus === DEVICE_STATUS.SUBSCRIPTION_EXPIRED) {
        try {
          // 브라우저 지원 확인
          if (!('Notification' in window) || !('serviceWorker' in navigator)) {
            return;
          }

          // 알림 권한 확인
          const currentPermission = Notification.permission;

          if (currentPermission === 'default') {
            // 권한 요청
            const { requestNotificationPermission } = await import('./firebase');
            await requestNotificationPermission();
          }

          if (Notification.permission === 'granted') {
            // FCM 토큰 가져오기
            const { getFCMToken } = await import('./firebase');
            const fcmToken = await getFCMToken();
            if (fcmToken) {
              setToken(fcmToken);
            }
          }
        } catch (error) {
          console.error('푸시 알림 초기화 실패:', error);
        }
      }
    };

    initializePushNotifications();
  }, [appStatus]);

  // 앱이 포그라운드로 돌아올 때 배지 카운트 동기화
  useEffect(() => {
    const handleVisibilityChange = () => {
      // active, trial, expired, subscription_expired 사용자도 배지 초기화
      if (!document.hidden && (appStatus === 'active' || appStatus === 'trial' || appStatus === 'expired' || appStatus === DEVICE_STATUS.SUBSCRIPTION_EXPIRED)) {
        // 앱이 포그라운드로 돌아올 때 배지 카운트 초기화
        if ('setAppBadge' in navigator) {
          navigator.clearAppBadge().catch(error => {
            console.warn('PWA 앱 배지 초기화 실패:', error);
          });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [appStatus]);

  // FCM 토큰 자동 등록 (권한이 허용된 후) - 중복 등록 방지
  useEffect(() => {
    console.log('FCM 토큰 등록 조건 확인:', {
      hasToken: !!token,
      hasTrialInfo: !!trialInfo,
      deviceId: trialInfo?.deviceId,
      appStatus: appStatus
    });

    // active, trial, expired, subscription_expired, rejected, pending 사용자도 FCM 토큰 등록
    if (token && trialInfo?.deviceId && (appStatus === 'active' || appStatus === 'trial' || appStatus === 'expired' || appStatus === DEVICE_STATUS.SUBSCRIPTION_EXPIRED || appStatus === DEVICE_STATUS.REJECTED || appStatus === DEVICE_STATUS.PENDING)) {
      // 이미 등록된 토큰인지 확인
      const lastRegisteredToken = localStorage.getItem('lastRegisteredToken');
      const lastRegisteredStatus = localStorage.getItem('lastRegisteredStatus');

      if (lastRegisteredToken === token && lastRegisteredStatus === appStatus) {
        console.log('FCM 토큰이 이미 등록되어 있습니다.');
        return;
      }

      // Firebase Functions를 통해 토큰 등록
      const registerToken = async () => {
        try {
          const { registerTokenToServer } = await import('./firebase');
          // status 매핑: 각 상태를 그대로 저장하여 푸시알림 대상 필터링이 정확히 작동하도록 함
          let status = appStatus;
          if (appStatus === 'active') {
            status = 'active';
          } else if (appStatus === 'trial') {
            status = 'trial';
          } else if (appStatus === 'expired') {
            status = 'expired';
          } else if (appStatus === DEVICE_STATUS.SUBSCRIPTION_EXPIRED) {
            status = 'subscription_expired';
          } else if (appStatus === DEVICE_STATUS.REJECTED) {
            status = 'rejected';
          } else if (appStatus === DEVICE_STATUS.PENDING) {
            status = 'pending';
          }
          const success = await registerTokenToServer(trialInfo.deviceId, status);

          if (success) {
            // 등록 성공 시 로컬 스토리지에 저장
            localStorage.setItem('lastRegisteredToken', token);
            localStorage.setItem('lastRegisteredStatus', appStatus);
            console.log('FCM 토큰이 서버에 등록되었습니다.');
          }
        } catch (error) {
          console.error('FCM 토큰 등록 실패:', error);
        }
      };

      registerToken();
    }
  }, [token, trialInfo, appStatus]);



  // 체험 상태 확인 - 로컬 우선 
  const checkTrialStatus = async () => {
    try {
      // 인앱 브라우저 차단 로직 제거 - 카카오톡 인앱 브라우저에서도 체험 기록 허용

      let deviceId = localStorage.getItem('deviceId');
      if (!deviceId) {
        deviceId = generateDeviceId();
        localStorage.setItem('deviceId', deviceId);
      }

      let trialStartDate = localStorage.getItem('trialStartDate');

      // trialStartDate가 없는 경우: Firebase에서 기존 기록 확인 또는 새로 생성
      if (!trialStartDate) {
        try {
          // Firebase에서 기존 기록 확인
          const { doc, getDoc } = await import('firebase/firestore');
          const deviceDoc = await getDoc(doc(db, COLLECTIONS.DEVICES, deviceId));

          if (deviceDoc.exists()) {
            const deviceData = deviceDoc.data();
            // Firebase에 trialStartDate가 있으면 사용
            if (deviceData.trialStartDate) {
              trialStartDate = deviceData.trialStartDate;
              localStorage.setItem('trialStartDate', trialStartDate);
              console.log('[체험 복구] Firebase에서 기존 체험 시작일 복구:', trialStartDate);
            } else {
              // Firebase에 기록은 있지만 trialStartDate가 없는 경우: 오늘을 시작일로 설정
              const now = new Date().toISOString();
              trialStartDate = now;
              localStorage.setItem('trialStartDate', now);
              // Firebase 업데이트
              try {
                const { updateDoc } = await import('firebase/firestore');
                await updateDoc(doc(db, COLLECTIONS.DEVICES, deviceId), {
                  trialStartDate: now,
                  lastActiveDate: now,
                  status: DEVICE_STATUS.TRIAL
                });
                console.log('[체험 복구] Firebase 기록 업데이트 완료');
              } catch (updateError) {
                console.warn('[체험 복구] Firebase 업데이트 실패:', updateError);
              }
            }
          } else {
            // Firebase에 기록이 없는 경우: 새로 생성
            const now = new Date().toISOString();
            trialStartDate = now;
            localStorage.setItem('trialStartDate', now);
            // Firebase 저장은 백그라운드에서 실행
            try {
              const ensuredId = await sendTrialStartLog(deviceId, now);
              if (ensuredId && ensuredId !== deviceId) {
                deviceId = ensuredId;
                try { localStorage.setItem('deviceId', deviceId); } catch (_) { }
              }
              console.log('[체험 복구] 새로운 체험 기록 생성 완료');
            } catch (_) { }
          }
        } catch (firebaseError) {
          console.warn('[체험 복구] Firebase 확인 실패, 새로 생성:', firebaseError);
          // Firebase 확인 실패 시 오늘을 시작일로 설정
          const now = new Date().toISOString();
          trialStartDate = now;
          localStorage.setItem('trialStartDate', now);
          try {
            const ensuredId = await sendTrialStartLog(deviceId, now);
            if (ensuredId && ensuredId !== deviceId) {
              deviceId = ensuredId;
              try { localStorage.setItem('deviceId', deviceId); } catch (_) { }
            }
          } catch (_) { }
        }
      }

      // trialStartDate가 여전히 없으면 (모든 시도 실패) 오늘을 시작일로 설정
      if (!trialStartDate) {
        const now = new Date().toISOString();
        trialStartDate = now;
        localStorage.setItem('trialStartDate', now);
        try {
          const ensuredId = await sendTrialStartLog(deviceId, now);
          if (ensuredId && ensuredId !== deviceId) {
            deviceId = ensuredId;
            try { localStorage.setItem('deviceId', deviceId); } catch (_) { }
          }
        } catch (_) { }
      }

      // 공통 유틸리티 함수 사용
      const { calculateRemainingDays, calculateDaysUsed } = await import('./utils/trialUtils');
      const diffDays = calculateDaysUsed(trialStartDate);
      const daysRemaining = calculateRemainingDays(trialStartDate);

      return {
        status: daysRemaining > 0 ? 'trial' : 'expired',
        deviceId,
        startDate: trialStartDate,
        daysUsed: diffDays,
        daysRemaining
      };

    } catch (error) {
      console.error('체험 상태 확인 오류:', error);
      return {
        status: 'trial',
        deviceId: 'error-' + Math.random().toString(36).substr(2, 8),
        startDate: new Date().toISOString(),
        daysUsed: 0,
        daysRemaining: 7
      };
    }
  };

  // 체험 시작 로그 전송 - 비동기로 처리
  const sendTrialStartLog = async (deviceId, startTime) => {
    try {
      // 인앱 브라우저 차단 로직 제거 - 카카오톡 인앱 브라우저에서도 체험 기록 허용

      const { doc, setDoc, getDoc } = await import('firebase/firestore');

      let targetDeviceId = deviceId;

      // 충돌 방지: 동일 ID 문서 존재 시 새 ID 발급(최대 3회 시도)
      for (let attempt = 0; attempt < 3; attempt++) {
        const existing = await getDoc(doc(db, COLLECTIONS.DEVICES, targetDeviceId));
        if (!existing.exists()) break;
        // userAgent, ip 동일하면 재사용 허용
        const data = existing.data() || {};
        if (data.userAgent === navigator.userAgent) break;
        targetDeviceId = generateDeviceId();
      }

      // 최초 유입경로: localStorage에서만 읽어 1회 기록
      let referralSourcePersisted = '';
      try {
        referralSourcePersisted = localStorage.getItem('referralSource') || '';
      } catch (_) { }

      const trialData = createDeviceDocument(targetDeviceId, {
        userInfo: {
          name: '체험 사용자',
          phone: '미입력',
          email: '미입력',
          company: '미입력',
          position: '미입력'
        },
        trialStartDate: startTime,
        userAgent: navigator.userAgent,
        lastActiveDate: startTime,
        status: DEVICE_STATUS.TRIAL,
        isTrialUser: true,
        isVerified: true,
        // 최초 진입 유입경로 1회성 보존 (localStorage에서만)
        ...(referralSourcePersisted ? { referralSource: referralSourcePersisted, referralAt: startTime } : {})
      });

      await setDoc(doc(db, COLLECTIONS.DEVICES, targetDeviceId), trialData);
      console.log('체험 시작 로그 전송 완료');
      return targetDeviceId;
    } catch (error) {
      console.error('체험 시작 로그 전송 실패:', error);
      // 실패해도 앱 진행에는 문제없음
      return deviceId;
    }
  };

  // 승인 요청 전송
  const sendApprovalRequest = async (deviceId, userInfo) => {
    try {
      const { doc, setDoc, getDoc, updateDoc } = await import('firebase/firestore');

      // 기존 문서 확인
      const deviceRef = doc(db, COLLECTIONS.DEVICES, deviceId);
      const deviceDoc = await getDoc(deviceRef);

      const updateData = {
        userInfo,
        trialStartDate: localStorage.getItem('trialStartDate') || deviceDoc.data()?.trialStartDate,
        requestedAt: new Date().toISOString(),
        userAgent: navigator.userAgent,
        lastActiveDate: new Date().toISOString(),
        status: DEVICE_STATUS.PENDING,
        // nextBillingDate는 관리자가 승인할 때 설정됨
        updatedAt: new Date().toISOString()
      };

      // 기존 문서가 있으면 updateDoc, 없으면 setDoc 사용
      if (deviceDoc.exists()) {
        // 기존 데이터 보존하면서 업데이트
        await updateDoc(deviceRef, updateData);
      } else {
        // 새 문서 생성
        const deviceData = createDeviceDocument(deviceId, updateData);
        await setDoc(deviceRef, deviceData);
      }

      console.log('승인 요청 전송 완료');
      return true;
    } catch (error) {
      console.error('승인 요청 전송 실패:', error);
      localStorage.setItem('pendingRequest', JSON.stringify({
        deviceId,
        userInfo,
        requestedAt: new Date().toISOString()
      }));
      return true;
    }
  };

  // 승인 상태 확인 - 실시간 리스너 제거, 폴링으로 변경
  const checkApprovalStatus = async (deviceId) => {
    try {
      const { doc, getDoc, updateDoc } = await import('firebase/firestore');
      const deviceDoc = await getDoc(doc(db, COLLECTIONS.DEVICES, deviceId));

      if (deviceDoc.exists()) {
        const data = deviceDoc.data();
        // 다음결제일 경과 시 자동 제한
        try {
          if (
            data.status === DEVICE_STATUS.ACTIVE &&
            data.nextBillingDate &&
            new Date(data.nextBillingDate).getTime() <= Date.now()
          ) {
            await updateDoc(doc(db, COLLECTIONS.DEVICES, deviceId), {
              status: DEVICE_STATUS.SUBSCRIPTION_EXPIRED,
              subscriptionExpiredAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            });
            return DEVICE_STATUS.SUBSCRIPTION_EXPIRED;
          }
        } catch (_) { }
        return data.status;
      }

      const savedStatus = localStorage.getItem('appStatus');
      return savedStatus || DEVICE_STATUS.TRIAL;
    } catch (error) {
      console.error('승인 상태 확인 실패:', error);
      const savedStatus = localStorage.getItem('appStatus');
      return savedStatus || DEVICE_STATUS.TRIAL;
    }
  };

  // 실시간 리스너 제거, 폴링으로 대체
  const setupStatusPolling = (deviceId) => {
    // 30초마다 상태 확인
    const pollInterval = setInterval(async () => {
      try {
        const serverStatus = await checkApprovalStatus(deviceId);

        if (serverStatus !== appStatus && serverStatus !== DEVICE_STATUS.TRIAL) {
          console.log('상태 변경 감지:', serverStatus);
          localStorage.setItem('appStatus', serverStatus);
          setAppStatus(serverStatus);
          clearInterval(pollInterval); // 상태 변경되면 폴링 중단
        }
      } catch (error) {
        console.error('상태 폴링 오류:', error);
      }
    }, 30000); // 30초 간격

    // 5분 후 폴링 자동 중단
    setTimeout(() => {
      clearInterval(pollInterval);
      console.log('상태 폴링 자동 중단');
    }, 300000);

    return () => clearInterval(pollInterval);
  };

  // 활성/체험 상태에서도 주기적으로 서버 상태 동기화 (일시정지/거부 반영)
  useEffect(() => {
    if (!trialInfo?.deviceId) return;
    if (!(appStatus === DEVICE_STATUS.TRIAL || appStatus === DEVICE_STATUS.ACTIVE || appStatus === DEVICE_STATUS.PENDING)) return;

    let cancelled = false;

    const syncNow = async () => {
      try {
        const serverStatus = await checkApprovalStatus(trialInfo.deviceId);
        if (!cancelled && serverStatus !== appStatus) {
          localStorage.setItem('appStatus', serverStatus);
          setAppStatus(serverStatus);
        }
      } catch (e) {
        console.warn('상태 동기화 실패', e);
      }
    };

    // 즉시 1회 실행
    const immediate = setTimeout(syncNow, 1000);

    // 열린 탭에서는 5분마다 확인하고, 복귀 시에는 즉시 확인한다.
    const interval = setInterval(syncNow, 300000);

    // 탭 포커스/가시성 변경 시 즉시 확인
    const onVisibility = () => {
      if (document.visibilityState === 'visible') syncNow();
    };
    window.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', syncNow);

    return () => {
      cancelled = true;
      clearTimeout(immediate);
      clearInterval(interval);
      window.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', syncNow);
    };
  }, [appStatus, trialInfo]);

  // 추가기기 첫 진입 배너(세션당 1회)
  useEffect(() => {
    const run = async () => {
      try {
        console.log('배너 체크 시작:', { appStatus, DEVICE_STATUS_ACTIVE: DEVICE_STATUS.ACTIVE });
        if (appStatus !== DEVICE_STATUS.ACTIVE) {
          console.log('appStatus가 ACTIVE가 아님:', appStatus);
          return;
        }
        const already = sessionStorage.getItem('secondaryWelcomeShown');
        console.log('이미 표시됨:', already);
        if (already === '1') return;
        const deviceId = (trialInfo && trialInfo.deviceId) || localStorage.getItem('deviceId');
        if (!deviceId) return;
        const info = await getDeviceLinkInfo(deviceId);
        console.log('기기 링크 정보:', info);
        if (info && info.isSecondary) {
          console.log('추가기기 알림 표시');
          alert('추가기기 연결이 완료되었습니다. 필요한 경우 동기화(구글 드라이브)에서 다운로드하여 데이터를 맞춰주세요.');
          sessionStorage.setItem('secondaryWelcomeShown', '1');
        } else {
          console.log('추가기기가 아님 또는 정보 없음');
        }
      } catch (error) {
        console.error('배너 체크 오류:', error);
      }
    };
    run();
  }, [appStatus, trialInfo]);

  // REJECTED 상세 원인 로드(추가기기 해제 분기)
  useEffect(() => {
    const loadRejectedMeta = async () => {
      try {
        if (appStatus !== DEVICE_STATUS.REJECTED) {
          setRejectedMeta(null);
          return;
        }
        const deviceId = (trialInfo && trialInfo.deviceId) || localStorage.getItem('deviceId');
        if (!deviceId) return;
        const { doc, getDoc } = await import('firebase/firestore');
        const snap = await getDoc(doc(db, COLLECTIONS.DEVICES, deviceId));
        if (snap.exists()) {
          const data = snap.data() || {};
          setRejectedMeta({ revokeReason: data.revokeReason || null, secondaryRevokedAt: data.secondaryRevokedAt || null });
        } else {
          setRejectedMeta(null);
        }
      } catch (_) {
        setRejectedMeta(null);
      }
    };
    loadRejectedMeta();
  }, [appStatus, trialInfo]);

  // rejected 상태이지만 체험 잔여일이 있으면 체험 사용 가능
  useEffect(() => {
    const checkRejectedTrialStatus = async () => {
      if (appStatus !== 'rejected') {
        setRejectedTrialInfo(null);
        return;
      }

      try {
        const { calculateRemainingDays } = await import('./utils/trialUtils');
        const trialStartDate = localStorage.getItem('trialStartDate');

        if (trialStartDate) {
          const daysRemaining = calculateRemainingDays(trialStartDate);
          if (daysRemaining > 0) {
            // 체험 잔여일이 있으면 체험 사용 가능
            const deviceId = (trialInfo && trialInfo.deviceId) || localStorage.getItem('deviceId');
            setRejectedTrialInfo({
              status: 'trial',
              deviceId,
              startDate: trialStartDate,
              daysRemaining
            });
          } else {
            // 체험기간 종료
            setRejectedTrialInfo({
              status: 'expired',
              deviceId: (trialInfo && trialInfo.deviceId) || localStorage.getItem('deviceId'),
              startDate: trialStartDate,
              daysRemaining: 0
            });
          }
        } else {
          // trialStartDate가 없으면 체험 사용 불가
          setRejectedTrialInfo(null);
        }
      } catch (error) {
        console.error('거부 상태 체험 확인 오류:', error);
        setRejectedTrialInfo(null);
      }
    };

    checkRejectedTrialStatus();
  }, [appStatus, trialInfo]);

  // 앱 초기화 - 최적화
  const initializeApp = async () => {
    try {
      setAppStatus('loading');

      const trial = await checkTrialStatus();
      setTrialInfo(trial);

      // 로컬 스토리지 우선 확인
      const savedStatus = localStorage.getItem('appStatus');
      const savedUserInfo = localStorage.getItem('userInfo');
      const savedBusinessType = localStorage.getItem('businessType');

      if (savedUserInfo) {
        setUserInfo(JSON.parse(savedUserInfo));
      }

      // 업종이 이미 선택되어 있으면 랜딩 페이지를 건너뛰고 바로 앱 상태로
      if (savedBusinessType) {
        // 저장된 상태가 있으면 우선 사용
        if (savedStatus && savedStatus !== 'loading') {
          setAppStatus(savedStatus);

          // 백그라운드에서 서버 상태 확인
          if (savedStatus === DEVICE_STATUS.PENDING || savedStatus === DEVICE_STATUS.REJECTED) {
            setTimeout(async () => {
              const serverStatus = await checkApprovalStatus(trial.deviceId);
              if (serverStatus !== savedStatus) {
                localStorage.setItem('appStatus', serverStatus);
                setAppStatus(serverStatus);
              } else {
                // pending/rejected 상태면 폴링 시작 (재활성화 또는 승인 감지)
                setupStatusPolling(trial.deviceId);
              }
            }, 1000);
          }
          return;
        }

        // 저장된 상태가 없으면 서버 확인 후 바로 앱 상태로
        const serverStatus = await checkApprovalStatus(trial.deviceId);
        if (serverStatus === DEVICE_STATUS.ACTIVE) {
          localStorage.setItem('appStatus', DEVICE_STATUS.ACTIVE);
          setAppStatus(DEVICE_STATUS.ACTIVE);
        } else if (serverStatus === DEVICE_STATUS.REJECTED) {
          localStorage.setItem('appStatus', DEVICE_STATUS.REJECTED);
          setAppStatus(DEVICE_STATUS.REJECTED);
          setupStatusPolling(trial.deviceId);
        } else if (serverStatus === DEVICE_STATUS.PENDING) {
          localStorage.setItem('appStatus', DEVICE_STATUS.PENDING);
          setAppStatus(DEVICE_STATUS.PENDING);
          setupStatusPolling(trial.deviceId);
        } else if (serverStatus === DEVICE_STATUS.SUBSCRIPTION_EXPIRED) {
          localStorage.setItem('appStatus', DEVICE_STATUS.SUBSCRIPTION_EXPIRED);
          setAppStatus(DEVICE_STATUS.SUBSCRIPTION_EXPIRED);
          return;
        } else if (trial.status === 'expired') {
          setAppStatus('expired');
          return;
        } else {
          setAppStatus(DEVICE_STATUS.TRIAL);
        }
        return;
      }

      // 저장된 상태가 있으면 우선 사용
      if (savedStatus && savedStatus !== 'loading') {
        setAppStatus(savedStatus);

        // 백그라운드에서 서버 상태 확인
        if (savedStatus === DEVICE_STATUS.PENDING || savedStatus === DEVICE_STATUS.REJECTED) {
          setTimeout(async () => {
            const serverStatus = await checkApprovalStatus(trial.deviceId);
            if (serverStatus !== savedStatus) {
              localStorage.setItem('appStatus', serverStatus);
              setAppStatus(serverStatus);
            } else {
              // pending/rejected 상태면 폴링 시작 (재활성화 또는 승인 감지)
              setupStatusPolling(trial.deviceId);
            }
          }, 1000);
        }
        return;
      }

      // 저장된 상태가 없으면 서버 확인
      const serverStatus = await checkApprovalStatus(trial.deviceId);

      if (serverStatus === DEVICE_STATUS.ACTIVE) {
        localStorage.setItem('appStatus', DEVICE_STATUS.ACTIVE);
        setAppStatus(DEVICE_STATUS.ACTIVE);
      } else if (serverStatus === DEVICE_STATUS.REJECTED) {
        localStorage.setItem('appStatus', DEVICE_STATUS.REJECTED);
        setAppStatus(DEVICE_STATUS.REJECTED);
        // 폴링 시작 (재활성화 감지)
        setupStatusPolling(trial.deviceId);
      } else if (serverStatus === DEVICE_STATUS.PENDING) {
        localStorage.setItem('appStatus', DEVICE_STATUS.PENDING);
        setAppStatus(DEVICE_STATUS.PENDING);
        // 폴링 시작
        setupStatusPolling(trial.deviceId);
      } else if (serverStatus === DEVICE_STATUS.SUBSCRIPTION_EXPIRED) {
        localStorage.setItem('appStatus', DEVICE_STATUS.SUBSCRIPTION_EXPIRED);
        setAppStatus(DEVICE_STATUS.SUBSCRIPTION_EXPIRED);
        return;
      } else if (trial.status === 'expired') {
        setAppStatus('expired');
        return;
      } else {
        setAppStatus(DEVICE_STATUS.TRIAL);
      }

    } catch (error) {
      console.error('앱 초기화 오류:', error);
      setAppStatus(DEVICE_STATUS.TRIAL);
    } finally {
      setBooting(false);
    }
  };

  const handleStartTrial = () => {
    // 인앱 브라우저 차단 로직 제거 - 카카오톡 인앱 브라우저에서도 체험 허용

    try {
      localStorage.setItem('userVerified', 'true');
      localStorage.setItem('verificationDate', new Date().toISOString());
    } catch (_) { }
    initializeApp();
  };

  // 업종 선택 콜백 제거

  const handleSubscriptionRenewal = async () => {
    try {
      const deviceId = (trialInfo && trialInfo.deviceId) || localStorage.getItem('deviceId');
      if (!deviceId) {
        alert('기기 정보를 찾을 수 없습니다.');
        return;
      }

      // 사용자 정보 가져오기 (localStorage 우선, 없으면 Firestore에서)
      let userInfo = null;
      try {
        const savedUserInfo = localStorage.getItem('userInfo');
        if (savedUserInfo) {
          userInfo = JSON.parse(savedUserInfo);
        }
      } catch (e) {
        console.warn('localStorage에서 사용자 정보 로드 실패:', e);
      }

      // localStorage에 없으면 Firestore에서 가져오기
      if (!userInfo || !userInfo.name || !userInfo.phone || !userInfo.company || !userInfo.position) {
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const deviceSnap = await getDoc(doc(db, COLLECTIONS.DEVICES, deviceId));
          if (deviceSnap.exists()) {
            const deviceData = deviceSnap.data();
            if (deviceData.userInfo) {
              userInfo = deviceData.userInfo;
            }
          }
        } catch (e) {
          console.warn('Firestore에서 사용자 정보 로드 실패:', e);
        }
      }

      // Firestore 업데이트
      const { doc, updateDoc } = await import('firebase/firestore');
      await updateDoc(doc(db, COLLECTIONS.DEVICES, deviceId), {
        status: DEVICE_STATUS.PENDING,
        renewalRequestedAt: new Date().toISOString(),
        requestedAt: new Date().toISOString(), // 요청일 갱신
        updatedAt: new Date().toISOString()
      });

      // 사용자 정보가 있으면 메일 발송
      if (userInfo && userInfo.name && userInfo.phone && userInfo.company && userInfo.position) {
        try {
          const emailResult = await sendApprovalEmail({
            name: userInfo.name,
            phone: userInfo.phone,
            company: userInfo.company,
            position: userInfo.position,
            deviceId: deviceId
          });

          if (emailResult?.data?.success) {
            console.log('연장 신청 이메일 발송 성공:', emailResult.data);
          } else {
            console.warn('연장 신청 이메일 발송 실패:', emailResult?.data?.message || '이메일 발송 실패');
          }
        } catch (error) {
          console.error('연장 신청 이메일 발송 오류:', error);
          // 이메일 발송 실패해도 연장 신청은 계속 진행
        }
      } else {
        console.warn('사용자 정보가 불완전하여 이메일을 발송할 수 없습니다.');
      }

      localStorage.setItem('appStatus', DEVICE_STATUS.PENDING);
      setAppStatus(DEVICE_STATUS.PENDING);
      alert('연장 신청이 완료되었습니다. 관리자 검토 후 24시간 내 활성화됩니다.');
    } catch (error) {
      console.error('연장 신청 실패:', error);
      alert('연장 신청 중 오류가 발생했습니다.');
    }
  };

  const submitUserInfo = async () => {
    // 중복 호출 방지
    if (isSubmitting) {
      return;
    }

    if (!userInfo.name.trim()) {
      alert('이름을 입력해주세요!');
      return;
    }
    if (!userInfo.phone.trim()) {
      alert('연락처를 입력해주세요!');
      return;
    }
    if (!userInfo.company.trim()) {
      alert('회사명을 입력해주세요!');
      return;
    }
    if (!userInfo.position.trim()) {
      alert('직책을 입력해주세요!');
      return;
    }

    setIsSubmitting(true);
    try {
      const userData = {
        ...userInfo,
        submittedAt: new Date().toISOString(),
        deviceId: trialInfo?.deviceId
      };

      localStorage.setItem('userInfo', JSON.stringify(userData));

      // Resend를 통한 이메일 알림 (Firebase Functions 사용)
      let emailSent = false;
      let emailError = null;
      try {
        const emailResult = await sendApprovalEmail({
          name: userInfo.name,
          phone: userInfo.phone,
          company: userInfo.company,
          position: userInfo.position,
          deviceId: trialInfo?.deviceId
        });

        if (emailResult?.data?.success) {
          emailSent = true;
          console.log('이메일 발송 성공:', emailResult.data);
        } else {
          emailError = emailResult?.data?.message || '이메일 발송 실패';
          console.error('이메일 발송 실패:', emailResult?.data);
        }
      } catch (error) {
        emailError = error?.message || '이메일 발송 중 오류 발생';
        console.error('이메일 발송 오류:', error);
      }

      const success = await sendApprovalRequest(trialInfo?.deviceId, userInfo);

      if (success) {
        // GA4: 사용신청 제출 성공 이벤트 추적 (구매로 카운팅)
        try {
          const gtag = window.gtag;
          const measurementId = window.GA_MEASUREMENT_ID;
          if (typeof gtag === 'function' && measurementId) {
            const deviceId = generateDeviceId();
            // purchase 이벤트로 전송 (GA4 대시보드의 구매 메트릭)
            gtag('event', 'purchase', {
              transaction_id: `application_${trialInfo?.deviceId}_${Date.now()}`,
              value: 7900,
              currency: 'KRW',
              items: [{
                item_id: 'fc_funnel_application',
                item_name: 'FC퍼널 사용신청',
                item_category: 'subscription',
                price: 7900,
                quantity: 1
              }],
              // 추가 커스텀 파라미터
              app_status: appStatus,
              user_name: userInfo.name,
              company: userInfo.company,
              position: userInfo.position,
              has_email: !!userInfo.email,
              email_sent: emailSent,
              device_id: deviceId
            });
          }
        } catch (gaError) {
          console.warn('GA4 이벤트 전송 실패:', gaError);
        }

        localStorage.setItem('appStatus', 'pending');
        setAppStatus('pending');
        setShowUserInfoModal(false);

        // 이메일 발송 결과를 포함한 메시지 표시
        if (emailSent) {
          alert('승인 요청이 전송되었습니다!\n이메일 알림도 정상적으로 발송되었습니다.\n24시간 내에 결과를 알려드릴게요.');
        } else if (emailError) {
          alert(`승인 요청은 전송되었습니다.\n다만 이메일 알림 발송에 실패했습니다: ${emailError}\n승인 요청은 정상적으로 처리되었으니 걱정하지 마세요.`);
        } else {
          alert('승인 요청이 전송되었습니다! 24시간 내에 결과를 알려드릴게요.');
        }

        // 폴링 시작
        setupStatusPolling(trialInfo?.deviceId);
      } else {
        alert('승인 요청 전송에 실패했습니다. 다시 시도해주세요.');
      }
    } catch (error) {
      console.error('사용자 정보 저장 오류:', error);
      alert('정보 저장 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const approvalModal = (
    <motion.div
      key="approval-modal"
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          setShowUserInfoModal(false);
        }
      }}
    >
      <div className="bg-white rounded-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="text-center mb-6">
          <div className="text-4xl mb-4">📝</div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">FC퍼널 사용신청하기</h3>
          <p className="text-gray-600">"월 사용료: 7,900원"</p>
          <div className="mt-4 space-y-3">
            <a
              href="http://pf.kakao.com/_XTTxoxj/110889711"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-gradient-to-r from-purple-500 to-purple-600 text-white py-2 px-4 rounded-xl font-bold hover:from-purple-600 hover:to-purple-700 transition-all duration-300 shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 text-sm"
            >
              서비스 소개 & 후기 확인
            </a>
            <button
              onClick={() => {
              // GA4: 첫달할인 혜택 받기 버튼 클릭 이벤트 추적
              try {
                const gtag = window.gtag;
                const measurementId = window.GA_MEASUREMENT_ID;
                if (typeof gtag === 'function' && measurementId) {
                  const deviceId = generateDeviceId();
                  gtag('event', 'discount_button_click', {
                    app_status: appStatus,
                    button_location: 'approval_modal',
                    event_category: 'discount',
                    event_label: '첫달할인 혜택 받기 버튼 클릭',
                    device_id: deviceId
                  });
                }
              } catch (gaError) {
                console.warn('GA4 이벤트 전송 실패:', gaError);
              }
                
                // 랜덤 할인 정보 선택
                const discountOptions = [
                  { price: 2900, percent: 63 },
                  { price: 3900, percent: 51 },
                  { price: 4900, percent: 38 }
                ];
                const randomDiscount = discountOptions[Math.floor(Math.random() * discountOptions.length)];
                setDiscountInfo(randomDiscount);
                setShowDiscountModal(true);
              }}
              className="w-full bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-white py-3 px-4 rounded-xl font-bold text-base hover:from-yellow-500 hover:via-orange-600 hover:to-red-600 transition-all duration-300 shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 transform hover:scale-105"
            >
              🎁 첫달할인 혜택 받기
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">
              이름 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={userInfo.name}
              onChange={(e) => setUserInfo({ ...userInfo, name: e.target.value })}
              className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              placeholder="실명을 입력해주세요"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">
              연락처 <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              value={userInfo.phone}
              onChange={(e) => setUserInfo({ ...userInfo, phone: e.target.value })}
              className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              placeholder="010-1234-5678"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">
              이메일 <span className="text-gray-400 text-xs">(선택)</span>
            </label>
            <input
              type="email"
              value={userInfo.email}
              onChange={(e) => setUserInfo({ ...userInfo, email: e.target.value })}
              className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              placeholder="example@email.com"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">
              회사명 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={userInfo.company}
              onChange={(e) => setUserInfo({ ...userInfo, company: e.target.value })}
              className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              placeholder="소속 회사명을 입력해주세요"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">
              직책 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={userInfo.position}
              onChange={(e) => setUserInfo({ ...userInfo, position: e.target.value })}
              className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              placeholder="직책을 입력해주세요 (예: 팀장, 지사장 등)"
            />
          </div>
        </div>

        <div className="mt-6 space-y-3">
          {/* <a
            href="http://pf.kakao.com/_XTTxoxj"
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-center py-3 px-4 rounded-xl font-bold text-lg bg-[#FEE500]/20 text-gray-800 hover:bg-[#FEE500]/30 transition-colors"
          >
            카톡친구맺기
          </a> */}
          <button
            onClick={() => {
              if (appStatus === DEVICE_STATUS.PENDING && (trialInfo && trialInfo.status === 'trial')) {
                try { alert('이미 승인 대기 중입니다. 체험기간 동안 계속 이용하실 수 있어요.'); } catch (_) { }
                return;
              }
              if (isSubmitting) {
                return;
              }
              
              // GA4: 신청서 제출 버튼 클릭 이벤트 추적
              try {
                const gtag = window.gtag;
                const measurementId = window.GA_MEASUREMENT_ID;
                if (typeof gtag === 'function' && measurementId) {
                  const deviceId = generateDeviceId();
                  gtag('event', 'application_submit_click', {
                    app_status: appStatus,
                    button_location: 'approval_modal',
                    event_category: 'application',
                    event_label: '신청서 제출 버튼 클릭',
                    device_id: deviceId
                  });
                }
              } catch (gaError) {
                console.warn('GA4 이벤트 전송 실패:', gaError);
              }
              
              submitUserInfo();
            }}
            disabled={isSubmitting || (appStatus === DEVICE_STATUS.PENDING && (trialInfo && trialInfo.status === 'trial'))}
            className={`w-full py-3 px-4 rounded-xl font-bold text-lg transition-colors ${isSubmitting || (appStatus === DEVICE_STATUS.PENDING && (trialInfo && trialInfo.status === 'trial'))
              ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
              : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
          >
            {isSubmitting
              ? '전송 중...'
              : (appStatus === DEVICE_STATUS.PENDING && (trialInfo && trialInfo.status === 'trial'))
                ? '승인 대기 중'
                : '신청서 제출'
            }
          </button>
          <button
            onClick={() => setShowUserInfoModal(false)}
            className="w-full bg-gray-200 text-gray-700 py-3 px-4 rounded-xl font-bold text-lg hover:bg-gray-300 transition-colors"
          >
            취소
          </button>
        </div>

        <div className="mt-4 text-center">
          <p className="text-xs text-gray-500 mb-2">
            입력하신 정보는 사용자 관리 목적으로만 사용됩니다
          </p>
          <p className="text-xs text-gray-500">
            앱 내에 기록하시게 되는 내용은 언제든지 엑셀파일로 다운로드 받을 수 있으므로, 이용을 중단하더라도 데이터를 안전하게 가져가실 수 있습니다.
          </p>
        </div>
      </div>
    </motion.div>
  );

  // 첫달할인 당첨 모달
  const discountModal = (
    <motion.div
      key="discount-modal"
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50 overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          setShowDiscountModal(false);
        }
      }}
    >
      {/* 컨페티 효과를 위한 별들 */}
      {[...Array(20)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute text-2xl"
          initial={{
            x: '50%',
            y: '50%',
            opacity: 1,
            scale: 1,
          }}
          animate={{
            x: `${50 + (Math.random() - 0.5) * 100}%`,
            y: `${50 + (Math.random() - 0.5) * 100}%`,
            opacity: 0,
            scale: 0,
            rotate: Math.random() * 360,
          }}
          transition={{
            duration: 1.5,
            delay: i * 0.05,
            ease: 'easeOut',
          }}
        >
          {['🎉', '✨', '⭐', '🎊', '💫'][Math.floor(Math.random() * 5)]}
        </motion.div>
      ))}

      <motion.div
        className="bg-gradient-to-br from-yellow-50 via-orange-50 to-red-50 rounded-2xl p-8 max-w-md w-full relative overflow-hidden border-4 border-yellow-400 shadow-2xl"
        initial={{ scale: 0.5, opacity: 0, rotate: -10 }}
        animate={{ scale: 1, opacity: 1, rotate: 0 }}
        exit={{ scale: 0.5, opacity: 0 }}
        transition={{ type: 'spring', duration: 0.6, bounce: 0.5 }}
      >
        {/* 배경 장식 */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <motion.div
            className="absolute top-10 left-10 text-6xl opacity-20"
            animate={{ rotate: [0, 360], scale: [1, 1.2, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            🎁
          </motion.div>
          <motion.div
            className="absolute bottom-10 right-10 text-6xl opacity-20"
            animate={{ rotate: [360, 0], scale: [1, 1.2, 1] }}
            transition={{ duration: 2, repeat: Infinity, delay: 1 }}
          >
            🎉
          </motion.div>
        </div>

        <div className="relative z-10 text-center">
          {/* 축하 메시지 */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: 'spring', bounce: 0.6 }}
            className="mb-4"
          >
            <div className="text-7xl mb-4">🎊</div>
            <motion.h2
              className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-600 via-orange-600 to-red-600 mb-2"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              축하합니다!
            </motion.h2>
            <motion.p
              className="text-xl font-bold text-gray-800 mb-1"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              첫달할인 혜택에 당첨되셨습니다!
            </motion.p>
          </motion.div>

          {/* 할인 정보 박스 */}
          <motion.div
            className="bg-white rounded-xl p-6 mb-6 shadow-lg border-2 border-yellow-400"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.6, type: 'spring', bounce: 0.4 }}
          >
            <div className="text-4xl font-black text-red-600 mb-2">
              {discountInfo?.percent || 38}% 할인
            </div>
            <div className="text-gray-600 text-sm mb-3">
              첫 달 월 사용료
            </div>
            <div className="flex items-center justify-center gap-2">
              <span className="text-gray-400 line-through text-lg">7,900원</span>
              <span className="text-2xl font-bold text-red-600">{discountInfo?.price?.toLocaleString() || '4,900'}원</span>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="text-xs text-gray-500">
                * 두 번째 달부터는 정가 7,900원(VAT포함)이 적용됩니다
              </div>
              <div className="text-xs text-gray-500 mt-2">
                * 당첨혜택 최대할인가: 63%(2,900원)
              </div>
            </div>
          </motion.div>

          {/* 안내 메시지 */}
          <motion.p
            className="text-sm text-gray-600 mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            이 화면을 캡처해두세요. 신청서 제출 후 결제안내문을 카톡으로 받을 때, 보여주시면 됩니다.
          </motion.p>

          {/* 확인 버튼 */}
          <motion.button
            onClick={() => setShowDiscountModal(false)}
            className="w-full bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-white py-4 px-6 rounded-xl font-bold text-lg hover:from-yellow-500 hover:via-orange-600 hover:to-red-600 transition-all duration-300 shadow-lg shadow-orange-500/40 transform hover:scale-105"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.8 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            확인
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );

  if (booting) {
    return (
      <motion.div
        className="min-h-screen bg-white flex items-center justify-center"
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <div className="text-center">
          <motion.div
            className="h-8 w-8 border-2 border-gray-300 border-t-blue-500 rounded-full mx-auto mb-3"
            variants={spinnerVariants}
            animate="animate"
          ></motion.div>
          <motion.div
            className="text-sm text-gray-500"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            로딩 중...
          </motion.div>
        </div>
      </motion.div>
    );
  }

  if (appStatus === 'landing') {
    return (
      <AnimatePresence mode="wait">
        <motion.div
          key="landing"
          variants={pageVariants}
          initial="initial"
          animate="in"
          exit="out"
        >
          <LandingPage onStartTrial={handleStartTrial} />
          {showBrowserModalLanding && <BrowserModal onClose={() => setShowBrowserModalLanding(false)} />}
        </motion.div>
      </AnimatePresence>
    );
  }

  if (appStatus === 'loading') {
    return (
      <motion.div
        className="min-h-screen bg-gray-50 flex items-center justify-center"
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <div className="text-center">
          <motion.div
            className="text-4xl mb-4"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            ⏳
          </motion.div>
          <motion.div
            className="text-xl font-bold text-gray-800"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            앱을 시작하는 중...
          </motion.div>
        </div>
      </motion.div>
    );
  }

  // expired 상태는 아래에서 CustomerManagement와 함께 렌더링

  if (appStatus === DEVICE_STATUS.SUBSCRIPTION_EXPIRED) {
    return (
      <>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full text-center shadow-lg">
            <div className="text-6xl mb-4">💳</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">정식 이용기간이 만료되었습니다!</h2>
            <div className="mb-4 text-sm text-gray-600">
              Device ID: <span className="font-mono">{(trialInfo && trialInfo.deviceId) || localStorage.getItem('deviceId') || generateDeviceId()}</span>
            </div>
            <p className="text-gray-600 mb-6 leading-relaxed">
              서비스를 계속 이용하려면 연장 신청을 해주세요.<br />
              관리자 검토 후 24시간 내 활성화됩니다.
            </p>
            <button
              onClick={() => handleSubscriptionRenewal()}
              className="w-full bg-blue-500 text-white py-3 px-4 rounded-xl font-bold text-lg hover:bg-blue-600"
            >
              연장신청하기
            </button>
            <a
              href="http://pf.kakao.com/_XTTxoxj/chat"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center justify-center w-full py-3 px-4 rounded-xl font-bold text-lg bg-[#FEE500]/20 text-gray-800 hover:bg-[#FEE500]/30 transition-colors"
            >
              카톡문의
            </a>
          </div>
        </div>
      </>
    );
  }

  if (appStatus === 'pending' && !(trialInfo && trialInfo.status === 'trial')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl p-6 max-w-md w-full text-center shadow-lg">
          <div className="text-6xl mb-4">⏳</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">승인 대기 중입니다</h2>
          <p className="text-gray-600 mb-4 leading-relaxed">
            관리자가 요청을 검토하고 있습니다.<br />
            24시간 내에 결과를 알려드릴게요.
          </p>
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h3 className="font-bold text-gray-800 mb-2">제출된 정보:</h3>
            <div className="text-sm text-gray-600 space-y-1">
              <div>이름: {userInfo.name}</div>
              <div>연락처: {userInfo.phone}</div>
              {userInfo.email && <div>이메일: {userInfo.email}</div>}
              <div>회사: {userInfo.company}</div>
              <div>직책: {userInfo.position}</div>
              <div className="mt-2 pt-2 border-t border-gray-200">
                <div className="text-xs text-gray-500">Device ID: {trialInfo?.deviceId || 'N/A'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // rejected 상태 처리: 체험 잔여기간 여부에 따라 일관성 있게 처리
  if (appStatus === 'rejected' && rejectedTrialInfo) {
    // 거부 상태이지만 체험 정보가 있으면 (체험 중 또는 체험 종료) CustomerManagement 사용 가능
    const trialInfoWithRejected = {
      ...rejectedTrialInfo,
      isRejected: true
    };

    // 체험 중이면 trial 상태로, 체험 종료되었으면 expired 상태로 처리
    const effectiveAppStatus = rejectedTrialInfo.status === 'trial' ? 'trial' : 'expired';

    return (
      <AnimatePresence mode="wait">
        <motion.div
          key="main-app"
          variants={pageVariants}
          initial="initial"
          animate="in"
          exit="out"
        >
          <CustomerManagement appStatus={effectiveAppStatus} trialInfo={trialInfoWithRejected} />
          <AnimatePresence>
            {showUserInfoModal && (
              <motion.div key="approval-modal-wrapper">
                {approvalModal}
              </motion.div>
            )}
            {showDiscountModal && (
              <motion.div key="discount-modal-wrapper">
                {discountModal}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </AnimatePresence>
    );
  }

  // rejected 상태이지만 체험 정보가 없는 경우 (추가기기 해제 등 특수 케이스)
  if (appStatus === 'rejected') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl p-6 max-w-md w-full text-center shadow-lg">
          <div className="text-6xl mb-4">⛔</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">이용이 제한되었습니다</h2>
          {rejectedMeta?.revokeReason === 'primary_unlinked' ? (
            <>
              <p className="text-gray-600 mb-6 leading-relaxed">
                본 기기에서 연결이 해제되어 이 기기는 사용할 수 없습니다.<br />
                본 기기에서 다시 추가기기 등록을 진행해 주세요.
              </p>
              <div className="mb-4 text-sm text-gray-700">
                Device ID: <span className="font-mono">{(trialInfo && trialInfo.deviceId) || localStorage.getItem('deviceId') || '-'}</span>
              </div>
              <a
                href="http://pf.kakao.com/_XTTxoxj/chat"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-full py-3 px-4 rounded-xl font-bold text-lg bg-[#FEE500]/20 text-gray-800 hover:bg-[#FEE500]/30 transition-colors"
              >
                카톡문의
              </a>
            </>
          ) : (
            <>
              <p className="text-gray-600 mb-6 leading-relaxed">
                {rejectedTrialInfo && rejectedTrialInfo.status === 'expired' ? (
                  <>
                    체험기간이 종료되어 서비스 이용이 제한되었습니다.<br />
                    계속 사용하시려면 사용신청을 해주세요.
                  </>
                ) : (
                  <>
                    죄송합니다. 현재 서비스 이용이 제한되었습니다.<br />
                    자세한 사항은 관리자에게 문의해주세요.
                  </>
                )}
              </p>
              <div className="space-y-3">
                <button
                  onClick={async () => {
                    try {
                      const deviceId = trialInfo?.deviceId || localStorage.getItem('deviceId');
                      let payload = {};
                      const infoLocal = JSON.parse(localStorage.getItem('userInfo') || '{}');
                      payload = {
                        name: userInfo.name || infoLocal.name || '',
                        phone: userInfo.phone || infoLocal.phone || '',
                        email: userInfo.email || infoLocal.email || '',
                        company: userInfo.company || infoLocal.company || '',
                        position: userInfo.position || infoLocal.position || ''
                      };
                      if (!payload.name || !payload.phone || !payload.company || !payload.position) {
                        try {
                          const { doc, getDoc } = await import('firebase/firestore');
                          const snap = await getDoc(doc(db, COLLECTIONS.DEVICES, deviceId));
                          if (snap.exists() && snap.data().userInfo) {
                            const ui = snap.data().userInfo;
                            payload = {
                              name: payload.name || ui.name || '',
                              phone: payload.phone || ui.phone || '',
                              email: payload.email || ui.email || '',
                              company: payload.company || ui.company || '',
                              position: payload.position || ui.position || ''
                            };
                          }
                        } catch (_) { }
                      }
                      const ok = await sendApprovalRequest(deviceId, payload);
                      if (ok) {
                        localStorage.setItem('appStatus', DEVICE_STATUS.PENDING);
                        localStorage.setItem('userInfo', JSON.stringify({ ...payload, deviceId }));
                        setAppStatus(DEVICE_STATUS.PENDING);
                        alert('재신청이 접수되었습니다. 승인 대기 중입니다.');
                        setupStatusPolling(deviceId);
                      } else {
                        alert('재신청 전송에 실패했습니다. 다시 시도해주세요.');
                      }
                    } catch (e) {
                      console.error('재신청 오류:', e);
                      alert('재신청 중 오류가 발생했습니다.');
                    }
                  }}
                  className="w-full bg-gray-500 text-white py-3 px-4 rounded-xl font-bold text-lg hover:bg-gray-600"
                >
                  재신청하기
                </button>
                <a
                  href="kakaotalk://channel/public/_XTTxoxj"
                  className="inline-flex items-center justify-center w-full py-3 px-4 rounded-xl font-bold text-lg bg-[#FEE500]/20 text-gray-800 hover:bg-[#FEE500]/30 transition-colors"
                  onClick={(e) => {
                    e.preventDefault();
                    const startTime = Date.now();
                    window.location.href = 'kakaotalk://channel/public/_XTTxoxj';
                    // 앱이 없을 경우 웹 링크로 폴백 (2초 후, 새 탭에서 열기)
                    setTimeout(() => {
                      if (Date.now() - startTime < 2100) {
                        window.open('http://pf.kakao.com/_XTTxoxj/chat', '_blank');
                      }
                    }, 2000);
                  }}
                >
                  카톡문의
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (appStatus === 'trial' || appStatus === 'active' || appStatus === 'expired' || (appStatus === 'pending' && (trialInfo && trialInfo.status === 'trial'))) {
    return (
      <AnimatePresence mode="wait">
        <motion.div
          key="main-app"
          variants={pageVariants}
          initial="initial"
          animate="in"
          exit="out"
        >
          <CustomerManagement appStatus={appStatus} trialInfo={trialInfo} />
          <AnimatePresence>
            {showUserInfoModal && (
              <motion.div key="approval-modal-wrapper">
                {approvalModal}
              </motion.div>
            )}
            {showDiscountModal && (
              <motion.div key="discount-modal-wrapper">
                {discountModal}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </AnimatePresence>
    );
  }

  return null;
};

export default App;
